﻿import { Component, Inject, Injectable } from '@angular/core';

import { Router, RouterLink } from '@angular/router';
import { of, zip } from 'rxjs';

import {
  BorrowerAssociationData,
  BorrowerIdentifyingInfo,
  DashboardData,
  LoanApplicationsStates,
  LoanFacts,
  MortgageReEntryStateData,
  PricingEligibilityResponse
} from '../../../shared/models';
import PricingEligibilityEnum = PricingEligibilityResponse.PricingEligibilityEnum;

import MortgageReEntryStateEnum = MortgageReEntryStateData.MortgageReEntryStateEnum;
import { LoanStateDirector } from '../loan-state-director.interface';
import { BorrowerService } from '../borrower/borrower.service';
import { DashboardService } from '../dashboard/dashboard.service';
import { SessionService } from '../session/shared/session/session-service/session.service';
import { ConfigKey, ConfigurationService } from '../configuration/configuration.service';

import LoanDashboardCategory = LoanFacts.LoanDashboardCategoryEnum;
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';
import { MixpanelService } from '@shared/services';
import { ROUTES } from '@app/clpbp-app.settings';
/*
  This service is a work in progress, meant to encapsulate the logic of deciding where to send users upon entering
  a given loan.  This way, logic can be shared by on-login auto-redirection, and by user-chosen redirects from
  the show-all-my-loans dashboard.  The dashboard will be wired to this first, then common login can be made to
  consume this vs. duplicating logic inline.
 */
// @Component ({
//   providers: [ RouterLink ]
// })
@Injectable()
export class LoginRedirectServiceComponent {

  private isDashboardEnabled: boolean;

  constructor(
    private borrowerService: BorrowerService,
    private configService: ConfigurationService,
    private sessionService: SessionService,
    private dashboardService: DashboardService,
    @Inject('PLStateDirectorService') private plStateDirectorService: LoanStateDirector<LoanApplicationsStates.PersonalStateEnum>,
    private router: Router,
    private featureToggleService: FeatureToggleService,
    private mixpanel: MixpanelService) {
    this.featureToggleService.isUnifiedLoginEnabled.subscribe(bool => this.isDashboardEnabled = bool);
    }

  public get mortgageUrl(): string {
    return 'https://www.loandepot.com/about/loan-servicing/making-first-payment/';
  }

  public get personalLoanUrl(): string {
    // TODO: LDTSULA-5533 - this should be hitting a centralized config API from CLP when it's ready
    return 'https://www.payment.loandepot.com/';
  }

  private isLightningLoan(loan: LoanFacts): boolean {
    return loan.loanDashboardCategory === LoanDashboardCategory.Unsubmitted;
  }

  private isPersonalLoan(loan: LoanFacts): boolean {
    return loan.mortgageProviderType === LoanFacts.MortgageProviderTypeEnum.PersonalLoans;
  }

  private isPendingApproval(loan: LoanFacts): boolean {
    return loan.loanDashboardCategory === LoanDashboardCategory.PendingApproval;
  }

  private shouldGoToOffers(loan: LoanFacts, data): boolean {
    return this.isPendingApproval(loan) &&
      !this.isPersonalLoan(loan) &&
      data.isPricingEnabled &&
      data.eligibility.pricingEligibility === PricingEligibilityEnum.NoIssues &&
      data.reEntry.mortgageReEntryState === MortgageReEntryStateEnum.Offers;
  }

  private proceedToLightning(loan: LoanFacts, borrowerId: string, isActingBorrowerACoBorrower: boolean = false): void {
    this.configService.getMaintenanceStatus().subscribe(bool => {
      if (bool) {
        this.mixpanel.previousUrl = window.location.href;
        this.router.navigate([`/${ ROUTES.PL_FULL }/oops`]);
      } else {
        if (loan.redirectURL) {
          window.location.href = loan.redirectURL;
        } else {
          this.enterPLLightning(borrowerId);
        }
      }
    });
  }

  public proceedToPortal(loan: LoanFacts, borrowerId: string): void {
    this.configService.getMaintenanceStatus().subscribe(bool => {
      if (bool) {
        this.mixpanel.previousUrl = window.location.href;
        this.router.navigate([`/${ ROUTES.PL_FULL }/oops`]);
      } else {
        this.enterPLPortal();
      }
    });
  }

  // @NOTE Tech Debt: LDTSULA-5368 Cleanup this file
  // This allows a loan to be directed solely based on the state of its LoanFacts, rather than on the combination of
  // loan associations and dashboard direction.
  public directByLoanFacts(loan: LoanFacts, borrowerId: string): void {
    this.sessionService.personalLoanSession.data.loanApplicationGuid = loan.loanID;

    const onSuccess = data => {
      const borrower = loan.borrowers.find(b => b.borrowerID === borrowerId);
      const isCoBorrower = !!borrower && borrower.position === BorrowerIdentifyingInfo.PositionEnum.CoBorrower;

      if (!isCoBorrower) {
        this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerSelected = true;
      }

      if (this.isLightningLoan(loan) || this.shouldGoToOffers(loan, data)) {
        this.proceedToLightning(loan, borrowerId, isCoBorrower);
      } else {
        this.proceedToPortal(loan, borrowerId);
      }
    };

    zip(
      this.dashboardService.getLoanFacts(false),
      this.featureToggleService.isPricingEnabled,
      of(false),
      of({}),
      (loanFacts, isPricingEnabled, eligibility, reEntry) => ({ loanFacts, isPricingEnabled, eligibility, reEntry })
    ).subscribe(onSuccess, console.error);
  }

  public startBorrowerRedirect(
    recStage: DashboardData.RecommendedStageEnum,
    onErrorCallback: () => void = () => {},
    loanAssociation: BorrowerAssociationData = null,
    rawDashboardData: DashboardData = null,
    isCoBorrower: boolean = false): void {

    this.featureToggleService.isUnifiedLoginEnabled.subscribe(
      enabled => this.directBorrowerByLoanState(
        recStage, enabled, onErrorCallback, loanAssociation, rawDashboardData, isCoBorrower
      ),
      err => { console.error(err); if (onErrorCallback) { onErrorCallback(); } }
    );
  }

  public enterPLLightning(borrowerId): void {
    this.startBorrowerRedirect(DashboardData.RecommendedStageEnum.PersonalLoanAplication, null);
  }

  public enterPLPortal(): void {
    this.startBorrowerRedirect(DashboardData.RecommendedStageEnum.PersonalLoanConsumerPortal, null);
  }

  public getAllBorrowersFromAllLoans(loans): any[] {
    return loans && loans.length
      ? loans
        .map(l => l.borrowers || [])
        .reduce((a, b) => a.concat(b))
      : [];
  }

  public isMultiBorrowerAccount(allBorrowers: any[]): boolean {
    const onlyDistinctBorrowers = (value, index, self) => {
      const name = o => `${o.firstName} ${o.lastName}`;
      return self.map(name).indexOf(name(value)) === index;
    };

    const isMulti = allBorrowers.filter(onlyDistinctBorrowers).length > 1;
    this.dashboardService.isMultiBorrowerAccount.next(isMulti);

    return isMulti;
  }

  public anyBorrowersHaveSameEmail(loans: any[]): boolean {
    const borrowerEmails = {};
    this.getAllBorrowersFromAllLoans(loans).forEach(borrower => {
      const name = `${borrower.firstName} ${borrower.lastName}`;
      if (borrowerEmails[borrower.email]) {
        borrowerEmails[borrower.email][name] = true;
      } else {
        borrowerEmails[borrower.email] = { [name]: true };
      }
    });

    return Object.keys(borrowerEmails).some(email => Object.keys(borrowerEmails[email]).length > 1);
  }

  public sameLoanShowsUpTwice(loans: any[]): boolean {
    let sameLoanShowsUpTwice = false;
    const loanIds = {};

    loans.forEach(loan => {
      if (loanIds[loan.loanID]) {
        sameLoanShowsUpTwice = true;
      } else {
        loanIds[loan.loanID] = true;
      }
    });

    return sameLoanShowsUpTwice;
  }

  public shouldNavigateToVerifyPage(loans: any[]): boolean {
    return this.isMultiBorrowerAccount(this.getAllBorrowersFromAllLoans(loans)) &&
      (this.anyBorrowersHaveSameEmail(loans) || this.sameLoanShowsUpTwice(loans));
  }

  public directBorrowerByLoanState(
    destState: DashboardData.RecommendedStageEnum,
    enableULD: boolean,
    onErrorCallback: () => void = () => {},
    selectedLoanAssociation: BorrowerAssociationData = null,
    dashboardData: DashboardData = null,
    isCoBorrower: boolean = false): void {

    const url: string = this.router.url;

    // This loanStateDirector will be set by the mortgage or PL switch cases.
    let loanStateDirector: LoanStateDirector<LoanApplicationsStates.PersonalStateEnum> = null;

    const onSuccess = () => {
      loanStateDirector.autoDirect(this.sessionService.personalLoanSession.data.loanApplicationGuid);
    };

    const onError = (e) => {
      console.error(e);
      onErrorCallback();
    };

    if (DashboardData.RecommendedStageEnum.Dashboard === destState) {
      if (!enableULD) {
        if (url.includes('/personal-loans/')) {
          destState = DashboardData.RecommendedStageEnum.PersonalLoanAplication;
        } else {
          // Scan the loan set in the dashboardData to determine whether this person goes to CP or not.
          if (!!dashboardData) {
            const loanList = dashboardData.loans.filter(
              loan => loan.loanDashboardCategory !== LoanFacts.LoanDashboardCategoryEnum.Unsubmitted);
            const usePortal = !!loanList && (loanList.length > 0);

            if (usePortal) {
              destState = DashboardData.RecommendedStageEnum.MortgageLoanConsumerPortal;
            } else {
              destState = DashboardData.RecommendedStageEnum.MortgageApplication;
            }
          } else {
            destState = DashboardData.RecommendedStageEnum.MortgageLoanConsumerPortal;
          }
        }
      }
    }

    const allBorrowersFromAllLoans = this.getAllBorrowersFromAllLoans(dashboardData && dashboardData.loans);

    switch (destState) {
      case DashboardData.RecommendedStageEnum.Login:
        break;

      case DashboardData.RecommendedStageEnum.Dashboard:
        // Only if the ssn is present for all borrowers do we direct the user to verify by ssn.
        // Otherwise, we direct the user to verify by name.
        const missingSsnForUser = allBorrowersFromAllLoans.some(b => !b.code);

        if (this.shouldNavigateToVerifyPage(dashboardData && dashboardData.loans)) {
          if (missingSsnForUser) {
            this.router.navigate(['verify', 'assert']);
          } else {
            this.router.navigate(['verify', 'ssn']);
          }
        } else {
          this.router.navigate(['dashboard']);
        }

        break;

      case DashboardData.RecommendedStageEnum.PersonalLoanAplication:
      case DashboardData.RecommendedStageEnum.PersonalLoanConsumerPortal:
        loanStateDirector = this.plStateDirectorService;
        this.borrowerService.initializeBorrowerSession(this.sessionService.personalLoanSession.data.loanApplicationGuid)
          .subscribe(onSuccess, onError);

        break;

      case DashboardData.RecommendedStageEnum.MortgageApplication:
      case DashboardData.RecommendedStageEnum.MortgageLoanConsumerPortal:
      default:
        this.directToConsumerPortal(selectedLoanAssociation);
    }
  }

  public directToConsumerPortal(selectedLoanAssociation?: BorrowerAssociationData): void {
    zip(
      this.configService.getConfigValueByKey(ConfigKey.CpUrl),
      // Removed SSO code since not supported by personal loans
      // Personal loans also does not own dashboard, so any dashboard relevant code should be cleaned up eventually
      this.isDashboardEnabled ? of({}) : of({}),
      (loginUrl, token) => ({ loginUrl, token })
    )
    .subscribe(response => {
      window.location.href = `${response.loginUrl}?token=${response.token['token']}&distinct_id=${this.mixpanel.distinct_id}`;
    }, console.error);
  }
}
